import React from 'react';
import parse, { domToReact, DOMNode } from 'html-react-parser';
import { enqueueSnackbar } from 'notistack';
import { Link, Stack } from '@mui/material';
import dayjs from 'dayjs';

import { baseApiUrl } from 'data_sources/constants';
import { translate as t } from 'hooks/useTranslations';
import datetimeHelper from 'helpers/datetimeHelper';
import contentHelper from 'helpers/contentHelper';
import AuthUtil from 'utils/AuthUtil';

import type { Notification } from './_types';

export default class notificationsHelper {
  static getTranslationKey = (notification: Notification) => {
    let translationKey = `notifications.types.${notification.notificationType}`;
    if (notification.notificationType === 'archived_pdf_retrieval') {
      translationKey += `.${(notification.notificationData?.success ?? true) ? 'success' : 'failure'}`;
    }
    if (notification.notificationType === 'notification_publish_manual') {
      translationKey += `.${notification.notificationData?.status || 'success'}`;
    }
    return translationKey;
  };
  static getSubject = (notification: Notification) => {
    // Handle job_complete notifications that have direct subject/body properties
    if (notification.notificationType === 'job_complete' && notification.notificationData?.subject) {
      return parse(notification.notificationData.subject, {
        replace(node: DOMNode, ind: number) {
          return notificationsHelper.handleContent(node, ind);
        },
      });
    }

    let interpolations = notification.notificationData;

    let subject = t(`${this.getTranslationKey(notification)}.subject`, 'Unknown notification', interpolations);

    return parse(subject ?? '', {
      replace(node: DOMNode, ind: number) {
        return notificationsHelper.handleContent(node, ind);
      },
    });
  };

  static getBody = (notification: Notification) => {
    // Handle job_complete notifications that have direct subject/body properties
    if (notification.notificationType === 'job_complete' && notification.notificationData?.body) {
      return parse(notification.notificationData.body, {
        replace(node: DOMNode, ind: number) {
          return notificationsHelper.handleContent(node, ind);
        },
      });
    }

    let interpolations = notification.notificationData;
    if (notification.notificationType === 'archived_pdf_retrieval') {
      interpolations.download_link = notification.notificationData.manualId
        ? `<a href="archivedManual:${encodeURIComponent(JSON.stringify(notification.notificationData))}">${t('notifications.types.archived_pdf_retrieval.success.download_link')}</a>`
        : '';
    }
    if (notification.notificationType === 'user_list_excel_report') {
      interpolations.download_link = `<a href="report:${encodeURIComponent(JSON.stringify(notification.notificationData))}">${t('notifications.types.user_list_excel_report.download_link')}</a>`;
    }
    if (notification.notificationType === 'policy_ack_audit_excel_report') {
      interpolations.download_link = `<a href="report:${encodeURIComponent(JSON.stringify(notification.notificationData))}">${t('notifications.types.policy_ack_audit_excel_report.download_link')}</a>`;
    }
    if (notification.notificationType === 'policy_ack_pending_excel_report') {
      interpolations.download_link = `<a href="report:${encodeURIComponent(JSON.stringify(notification.notificationData))}">${t('notifications.types.policy_ack_pending_excel_report.download_link')}</a>`;
    }
    if (notification.notificationType === 'dtb_list_excel_report') {
      interpolations.download_link = `<a href="report:${encodeURIComponent(JSON.stringify({ ...notification.notificationData, createdAt: notification.createdAt, notificationType: notification.notificationType }))}">${t('notifications.types.dtb_list_excel_report.download_link')}</a>`;
    }
    if (notification.notificationType === 'awb_assessment_portfolio') {
      interpolations.download_link = notification.notificationData.downloadUrl
        ? `<a href="report:${encodeURIComponent(JSON.stringify(notification.notificationData.downloadUrl))}" title="${notification.notificationData.reportName || 'AWB Report'}">${t('notifications.types.awb_assessment_portfolio.download_link')}</a>`
        : '';
    }
    if (notification.notificationType === 'notification_publish_manual') {
      if (!notification.notificationData?.manualTitle && notification.notificationData?.title) {
        interpolations.manualTitle = notification.notificationData.title;
      }
    }
    if (notification.notificationType === 'notification_publish_new_body_version') {
      interpolations.versionName = notification.notificationData?.accreditingBodyId
        ? `<a href="/admin/awb/settings" title="New Accreditation Version">${notification.notificationData.versionName}</a>`
        : '';
    }

    let body = t(`${this.getTranslationKey(notification)}.body`, 'Contact support for details', interpolations);

    return parse(body ?? '', {
      replace(node: DOMNode, ind: number) {
        return notificationsHelper.handleContent(node, ind);
      },
    });
  };

  static getBodyText = (notification: Notification) => {
    // Handle job_complete notifications that have direct subject/body properties
    if (notification.notificationType === 'job_complete' && notification.notificationData?.body) {
      const ctr = document.createElement('div');
      ctr.innerHTML = notification.notificationData.body;
      return ctr.innerText;
    }

    let interpolations = notification.notificationData;
    if (notification.notificationType === 'archived_pdf_retrieval') {
      interpolations.download_link = notification.notificationData.manualId
        ? `${t('notifications.types.archived_pdf_retrieval.success.download_link')}`
        : '';
    }
    if (notification.notificationType === 'user_list_excel_report') {
      interpolations.download_link = t('notifications.types.user_list_excel_report.download_link');
    }
    if (notification.notificationType === 'policy_ack_audit_excel_report') {
      interpolations.download_link = t('notifications.types.policy_ack_audit_excel_report.download_link');
    }
    if (notification.notificationType === 'policy_ack_pending_excel_report') {
      interpolations.download_link = t('notifications.types.policy_ack_pending_excel_report.download_link');
    }
    if (notification.notificationType === 'dtb_list_excel_report') {
      interpolations.download_link = t('notifications.types.dtb_list_excel_report.download_link');
    }
    if (notification.notificationType === 'awb_assessment_portfolio') {
      interpolations.download_link = notification.notificationData.downloadUrl
        ? t('notifications.types.awb_assessment_portfolio.download_link')
        : '';
    }
    if (notification.notificationType === 'notification_publish_manual') {
      if (!notification.notificationData?.manualTitle && notification.notificationData?.title) {
        interpolations.manualTitle = notification.notificationData.title;
      }
    }
    if (notification.notificationType === 'notification_publish_new_body_version') {
      interpolations.versionName = notification.notificationData.accreditingBodyId
        ? notification.notificationData.versionName
        : '';
    }

    let body = t(`${this.getTranslationKey(notification)}.body`, 'Contact support for details', interpolations);

    const ctr = document.createElement('div');
    ctr.innerHTML = body;

    return ctr.innerText;
  };

  static getMessageType = (notification: Notification) => {
    let messageType = t(`notifications.messageTypes.${notification.messageType}`);

    return messageType;
  };

  static getTimeStamp = (notification: Notification) => {
    return datetimeHelper.formatDateTime(notification.createdAt);
  };

  static groupNotifications = (notifications: Notification[]) => {
    const dayInMs = 24 * 60 * 60 * 1000;
    const tsToday = Date.parse(
      new Date().toLocaleDateString([], { year: 'numeric', month: 'numeric', day: 'numeric' })
    );
    const tsYesterday = tsToday - dayInMs;
    const tsThisWeek = tsToday - new Date(tsToday).getDay() * dayInMs;
    const tsLastWeek = tsThisWeek - 7 * dayInMs;
    const notificationGroups: { [key: string]: Notification[]; } = {
      unread: [],
      today: [],
      yesterday: [],
      thisWeek: [],
      lastWeek: [],
      older: [],
    };

    notifications
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt || ''))
      .forEach((n) => {
        const notificationData = {
          ...n,
        };
        if (notificationData.state === 'unread') {
          notificationGroups.unread.push(notificationData);
        } else if (Date.parse(notificationData.createdAt) >= tsToday) {
          notificationGroups.today.push(notificationData);
        } else if (Date.parse(notificationData.createdAt) >= tsYesterday) {
          notificationGroups.yesterday.push(notificationData);
        } else if (Date.parse(notificationData.createdAt) >= tsThisWeek) {
          notificationGroups.thisWeek.push(notificationData);
        } else if (Date.parse(notificationData.createdAt) >= tsLastWeek) {
          notificationGroups.lastWeek.push(notificationData);
        } else {
          notificationGroups.older.push(notificationData);
        }
      });
    return notificationGroups;
  };

  static handleContent = (node: DOMNode, ind: number) => {
    if (node.type === 'tag') {
      if (node.name === 'a') {
        if (node.attribs.href && node.attribs.href.startsWith('archivedManual:')) {
          const data = JSON.parse(decodeURIComponent(node.attribs.href.split(':')[1]));
          return (
            <Link
              onClick={async (e: React.SyntheticEvent) => {
                e.preventDefault();
                await notificationsHelper.handleDownloadArchivedPdf(data);
              }}
            >
              <Stack
                component='span'
                direction='row'
                spacing={2}
                sx={{ display: 'inline-flex' }}
              >
                {domToReact(node.children as DOMNode[])}
              </Stack>
            </Link>
          );
        } else if (node.attribs.href && node.attribs.href.startsWith('report:')) {
          const data = JSON.parse(decodeURIComponent(node.attribs.href.split(':')[1]));
          // AWB reports pass a string (the URL), all other reports pass an object with a 'file' property
          const isAwbReport = typeof data === 'string';
          return (
            <Link
              onClick={async (e: React.SyntheticEvent) => {
                e.preventDefault();
                await notificationsHelper.handleDownloadReport(data, isAwbReport);
              }}
            >
              <Stack
                component='span'
                direction='row'
                spacing={2}
                sx={{ display: 'inline-flex' }}
              >
                {domToReact(node.children as DOMNode[])}
              </Stack>
            </Link>
          );
        } else if (node.attribs.href && node.attribs.href.startsWith('s3://')) {
          return (
            <Link
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
                notificationsHelper.handleDownloadAttachment(
                  node.attribs.href.split('://')[1],
                  node.attribs.title || 'file'
                );
              }}
            >
              <Stack
                component='span'
                direction='row'
                spacing={2}
                sx={{ display: 'inline-flex' }}
              >
                {domToReact(node.children as DOMNode[])}
              </Stack>
            </Link>
          );
        } else {
          node.attribs.target = '_blank';
          node.attribs.rel = 'noopener noreferrer';
        }
      }
    }
    return node;
  };
  static handleDownloadAttachment = (key: string, filename: string) => {
    const token = AuthUtil.getToken();
    const url = `${baseApiUrl()}/notifications/v1/media?key=${encodeURIComponent(key)}`;
    fetch(url, {
      method: 'GET',
      headers: {
        'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
        Authorization: token || '',
      },
    })
      .then((resp) => {
        return resp.blob();
      })
      .then((blob) => {
        const href = URL.createObjectURL(blob);

        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  };

  static handleDownloadArchivedPdf = async (data: any) => {
    const token = AuthUtil.getToken();
    const url = `${baseApiUrl()}/lexipub/manuals/archived/v1/${data.legacy === true ? 'legacy-manuals' : 'manuals'}/${data.manualId}/versions/${data.version}/download`;
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
          Authorization: token || '',
        },
      });
      const downloadData = await resp.json();
      if (downloadData.url) {
        const resp = await fetch(downloadData.url, {
          method: 'GET',
        });
        const blob = await resp.blob();
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `${contentHelper.parameterize(data.title)}_${data.version}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      enqueueSnackbar(parse(t(`notifications.errors.downloadFailure`)), {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }
  };

  static handleDownloadReport = async (data: any, isAwbReport: boolean = false) => {
    const token = AuthUtil.getToken();
    const fileParam = isAwbReport ? data : data.file;
    const url = `${baseApiUrl()}/notifications/v1/attachments/download?file=${encodeURIComponent(fileParam)}`;

    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
          Authorization: token || '',
        },
      });
      const downloadData = await resp.json();
      let fileName = decodeURIComponent(downloadData.url.split('#')[0].split('?')[0].split('/').pop() || 'download');

      if (!isAwbReport && data.notificationType === 'dtb_list_excel_report' && data.createdAt) {
        const date = dayjs(data.createdAt);
        const formattedDate = date.format('MM-DD-YYYY');
        const formattedTime = date.format('HH-mm');

        // Extract the original file extension
        const originalExtension = fileName.includes('.') ? fileName.split('.').pop() : 'xls';

        fileName = `DTB_Report_Full_Audit_Downloaded_${formattedDate}_${formattedTime}.${originalExtension}`;
      }

      if (downloadData.url) {
        fetch(downloadData.url, {
          method: 'GET',
        })
          .then((resp) => {
            return resp.blob();
          })
          .then((blob) => {
            const href = URL.createObjectURL(blob);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          });
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      enqueueSnackbar(parse(t(`notifications.errors.downloadFailure`)), {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }
  };
}
